import { useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  getReactSelectValue,
  ReactSelectInput,
} from '@hivebrite/design-system/Inputs';

import { galleryImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import EditImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/EditImageSource';
import TextSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/TextSetting';
import AccordionTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/AccordionTitle';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import {
  BACKGROUND_POSITION,
  positionOptions,
} from 'hb-builder/constants/imageArrangement';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';
import type { GridItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/galleryItemAttributes';

const getMaxLength = (size) => (size === 'big' ? 50 : 20);

interface ContentItemProps
  extends Omit<ComponentSettingsTab<GridItemAttributes>, 'defaultAttributes'> {
  index: number;
}

const ContentItem = ({ item, onChange, index }: ContentItemProps) => {
  const [positionValue, setPositionValue] = useState(
    item?.attributes?.backgroundPosition || BACKGROUND_POSITION.CENTER,
  );

  const settingsProps = { item, onChange };
  const hasImage = Boolean(item?.attributes?.url);

  const handleChangePosition = (e) => {
    setPositionValue(e.value);

    onChange({
      id: item.id,
      settingKey: 'attributes.backgroundPosition',
      value: e.value,
    });
  };

  const positionValueSelected = getReactSelectValue(
    positionOptions,
    positionValue,
  );

  return (
    <AccordionTitle isDrawerOpen={index === 0} title={`Image ${index + 1}`}>
      <ImageSettingWrapper>
        <InputTitle id="settingsImagePosition" label={__('Image position')} />
        <ReactSelectInput
          id="settingsImagePosition"
          name="settingsImagePosition"
          onChange={handleChangePosition}
          options={positionOptions}
          value={positionValueSelected}
          width={155}
        />
      </ImageSettingWrapper>
      <Box my={2}>
        {s__(
          'Builder|Recommended %{size}px. The file must not exceed 2.5 MB',
        ).i__({
          size: galleryImageRecommendedSize[
            item?.attributes?.size === 'big'
              ? 'TemplateGridBig'
              : 'TemplateGridSmall'
          ],
        })}
      </Box>
      <EditImageSource hasImage={hasImage} id={item.id} />
      <TextSetting
        dataTestId="gallery-alt-text-setting"
        description={s__(
          "Builder|This will appear if the image can't be displayed",
        )}
        settingKey="attributes.imageAlt"
        title={__('Alternative text')}
        {...settingsProps}
      />
      <TextSetting
        maxLength={getMaxLength(item?.attributes?.size)}
        settingKey="attributes.caption"
        title={__('Caption')}
        verticalAlign
        {...settingsProps}
      />
      <ImageSetter
        attributeName="url"
        item={item}
        imageSize={
          galleryImageRecommendedSize[
            item.attributes?.size === 'big'
              ? 'TemplateGridBig'
              : 'TemplateGridSmall'
          ]
        }
      />
    </AccordionTitle>
  );
};

export default ContentItem;

const ImageSettingWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  mt: -2,
}))``;
